import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Settings as SettingsIcon,
  User,
  Shield,
  Activity,
  Clock,
  Save,
  RotateCcw,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle,
  Plus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth, useAuthActions } from '@/store/authStore';
import { Permission, Role } from '@/types/permissions';
import { api } from '@/services/api';

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { updateUser } = useAuthActions();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [saveNoticeAt, setSaveNoticeAt] = useState<string | null>(null);
  const [isProfileSaving, setIsProfileSaving] = useState(false);
  const [isSystemSaving, setIsSystemSaving] = useState(false);
  const [isSettingsLoading, setIsSettingsLoading] = useState(false);
  const [isLogsLoading, setIsLogsLoading] = useState(false);
  const [activityLogs, setActivityLogs] = useState<
    Array<{ id: number; action?: string; staff_id?: number; created_at?: string; changes?: string }>
  >([]);
  
  // Settings form state
  const [profileForm, setProfileForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [systemSettings, setSystemSettings] = useState({
    business_hours_start: '09:00',
    business_hours_end: '22:00',
    booking_advance_days: 30,
    cancellation_hours: 24,
    default_booking_duration: 60,
    auto_confirm_walkin: true,
    email_notifications: true,
    sms_notifications: false,
    receipt_printer: true,
    theme: 'light',
    language: 'zh-CN',
    timezone: 'Asia/Shanghai'
  });

  const canManageStaff = useMemo(() => {
    if (!user) return false;
    if (user.role === Role.ADMIN) return true;
    const permissions = Array.isArray(user.permissions) ? user.permissions : [];
    return permissions.includes(Permission.MANAGE_STAFF);
  }, [user]);

  const tabs = [
    { id: 'profile', label: t('settings.profile'), icon: User, description: t('settings.profileSettings') },
    { id: 'staff', label: t('settings.staffManagement'), icon: Shield, description: t('settings.staffManagementDesc') },
    { id: 'system', label: t('settings.systemSettings'), icon: SettingsIcon, description: t('settings.systemSettingsDesc') },
    { id: 'activity', label: t('settings.activityLogs'), icon: Activity, description: t('settings.activityLogsDesc') }
  ];

  useEffect(() => {
    setProfileForm({
      full_name: user?.full_name || '',
      email: user?.email || '',
      current_password: '',
      new_password: '',
      confirm_password: '',
    });
  }, [user?.full_name, user?.email]);

  useEffect(() => {
    const loadSettings = async () => {
      setIsSettingsLoading(true);
      const response = await api.settings.get();
      setIsSettingsLoading(false);

      if (response.error) {
        toast.error(response.error);
        return;
      }

      const data = (response.data || {}) as Record<string, unknown>;
      setSystemSettings(prev => ({
        ...prev,
        business_hours_start:
          typeof data.business_hours_start === 'string'
            ? data.business_hours_start
            : prev.business_hours_start,
        business_hours_end:
          typeof data.business_hours_end === 'string'
            ? data.business_hours_end
            : prev.business_hours_end,
        booking_advance_days:
          typeof data.booking_advance_days === 'number'
            ? data.booking_advance_days
            : prev.booking_advance_days,
        cancellation_hours:
          typeof data.cancellation_hours === 'number'
            ? data.cancellation_hours
            : prev.cancellation_hours,
        default_booking_duration:
          typeof data.default_booking_duration === 'number'
            ? data.default_booking_duration
            : prev.default_booking_duration,
        auto_confirm_walkin:
          typeof data.auto_confirm_walkin === 'boolean'
            ? data.auto_confirm_walkin
            : prev.auto_confirm_walkin,
      }));
    };

    void loadSettings();
  }, []);

  const loadActivityLogs = async () => {
    setIsLogsLoading(true);
    const response = await api.settings.getLogs({ page: 1, limit: 50 });
    setIsLogsLoading(false);

    if (response.error) {
      toast.error(response.error);
      return;
    }

    const payload = response.data as
      | { logs?: Array<{ id: number; action?: string; staff_id?: number; created_at?: string; changes?: string }> }
      | undefined;
    setActivityLogs(Array.isArray(payload?.logs) ? payload.logs : []);
  };

  useEffect(() => {
    if (activeTab === 'activity') {
      void loadActivityLogs();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!saveNotice) return;
    const timer = window.setTimeout(() => {
      setSaveNotice(null);
      setSaveNoticeAt(null);
    }, 8000);
    return () => window.clearTimeout(timer);
  }, [saveNotice]);

  const handleProfileSave = async () => {
    if (!user) return;

    const fullNameChanged = profileForm.full_name.trim() !== (user.full_name || '');
    const emailChanged = profileForm.email.trim() !== (user.email || '');
    const needChangePassword =
      Boolean(profileForm.current_password) ||
      Boolean(profileForm.new_password) ||
      Boolean(profileForm.confirm_password);

      if (!fullNameChanged && !emailChanged && !needChangePassword) {
        toast(t('settings.noChanges'));
        return;
      }
      setSaveNotice(null);

    setIsProfileSaving(true);

    try {
      if (fullNameChanged || emailChanged) {
        if (!canManageStaff) {
          toast.error('You do not have permission to update profile name/email.');
          return;
        }

        const profileRes = await api.staff.update(user.id, {
          full_name: profileForm.full_name.trim(),
          email: profileForm.email.trim(),
        });

        if (profileRes.error) {
          toast.error(profileRes.error);
          return;
        }

        updateUser({
          full_name: profileForm.full_name.trim(),
          email: profileForm.email.trim(),
        });
      }

      if (needChangePassword) {
        if (!profileForm.current_password || !profileForm.new_password) {
          toast.error(t('settings.passwordFieldsRequired'));
          return;
        }
        if (profileForm.new_password !== profileForm.confirm_password) {
          toast.error(t('settings.passwordMismatch'));
          return;
        }

        const passwordRes = await api.auth.changePassword({
          current_password: profileForm.current_password,
          new_password: profileForm.new_password,
        });

        if (passwordRes.error) {
          toast.error(passwordRes.error);
          return;
        }

        // 检查 WordPress 同步状态
        const resData = passwordRes.data as { wp_sync?: boolean } | undefined;
        if (resData?.wp_sync === false) {
          toast.error(t('settings.wpSyncFailed'));
        }
      }

      toast.success(t('common.saved'));
      setSaveNotice(t('common.saved'));
      setSaveNoticeAt(new Date().toLocaleTimeString());
      setProfileForm(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: '',
      }));
      setUnsavedChanges(false);
    } finally {
      setIsProfileSaving(false);
    }
  };

  const handleSystemSave = async () => {
    setSaveNotice(null);
    setIsSystemSaving(true);
    const response = await api.settings.update(systemSettings);
    setIsSystemSaving(false);

    if (response.error) {
      toast.error(response.error);
      return;
    }

    toast.success(t('common.saved'));
    setSaveNotice(t('common.saved'));
    setSaveNoticeAt(new Date().toLocaleTimeString());
    setUnsavedChanges(false);
  };

  const renderProfileTab = () => (
    <div className="space-y-6">
      <div className="card card-comfortable">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('settings.basicInfo')}</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
{t('settings.name')}
            </label>
            <input
              type="text"
              value={profileForm.full_name}
              onChange={(e) => {
                setProfileForm(prev => ({ ...prev, full_name: e.target.value }));
                setUnsavedChanges(true);
              }}
              className="w-full input"
              placeholder={t('settings.enterName')}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.emailAddress')}
            </label>
            <input
              type="email"
              value={profileForm.email}
              onChange={(e) => {
                setProfileForm(prev => ({ ...prev, email: e.target.value }));
                setUnsavedChanges(true);
              }}
              className="w-full input"
              placeholder={t('settings.enterEmail')}
            />
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-md font-medium text-gray-900 mb-4">{t('settings.changePassword')}</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.currentPassword')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={profileForm.current_password}
                  onChange={(e) => {
                    setProfileForm(prev => ({ ...prev, current_password: e.target.value }));
                    setUnsavedChanges(true);
                  }}
                  className="w-full input pr-12"
                  placeholder={t('settings.enterCurrentPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.newPassword')}
              </label>
              <input
                type="password"
                value={profileForm.new_password}
                onChange={(e) => {
                  setProfileForm(prev => ({ ...prev, new_password: e.target.value }));
                  setUnsavedChanges(true);
                }}
                className="w-full input"
                placeholder={t('settings.enterNewPassword')}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.confirmNewPassword')}
              </label>
              <input
                type="password"
                value={profileForm.confirm_password}
                onChange={(e) => {
                  setProfileForm(prev => ({ ...prev, confirm_password: e.target.value }));
                  setUnsavedChanges(true);
                }}
                className="w-full input"
                placeholder={t('settings.reenterNewPassword')}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-8">
          <button
            onClick={() => void handleProfileSave()}
            disabled={!unsavedChanges || isProfileSaving}
            className="btn btn-primary gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" />
            {isProfileSaving ? t('common.saving') : t('settings.saveChanges')}
          </button>
          
          <button
            onClick={() => {
              setProfileForm({
                full_name: user?.full_name || '',
                email: user?.email || '',
                current_password: '',
                new_password: '',
                confirm_password: ''
              });
              setUnsavedChanges(false);
            }}
            disabled={!unsavedChanges || isProfileSaving}
            className="btn btn-secondary gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCcw className="h-4 w-4" />
            {t('settings.reset')}
          </button>
        </div>
      </div>
    </div>
  );

  const renderStaffTab = () => (
    <div className="space-y-6">
      {user?.role === Role.ADMIN ? (
        <>
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t('settings.staffManagementTitle')}</h3>
              <p className="text-gray-500 text-sm">{t('settings.staffManagementDescription')}</p>
            </div>
            <button
              onClick={() => navigate('/staff')}
              className="btn btn-primary gap-2"
            >
              <Plus className="h-4 w-4" />
              {t('settings.openStaffManagement')}
            </button>
          </div>

          <div className="card card-comfortable">
            <div className="text-sm text-gray-600">
              {t('settings.staffManagementMoved')}
            </div>
          </div>
        </>
      ) : (
        <div className="card card-comfortable text-center py-12">
          <Shield className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('settings.insufficientPermissions')}</h3>
          <p className="text-gray-500">{t('settings.onlyAdminCanManageStaff')}</p>
        </div>
      )}
    </div>
  );

  const renderSystemTab = () => (
    <div className="space-y-6">
      <div className="card card-comfortable">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('settings.businessSettings')}</h3>
        {isSettingsLoading && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            {t('common.loading')}
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="inline h-4 w-4 mr-1" />
              {t('settings.businessHoursStart')}
            </label>
            <input
              type="time"
              value={systemSettings.business_hours_start}
              onChange={(e) => {
                setSystemSettings(prev => ({ ...prev, business_hours_start: e.target.value }));
                setUnsavedChanges(true);
              }}
              className="w-full input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="inline h-4 w-4 mr-1" />
              {t('settings.businessHoursEnd')}
            </label>
            <input
              type="time"
              value={systemSettings.business_hours_end}
              onChange={(e) => {
                setSystemSettings(prev => ({ ...prev, business_hours_end: e.target.value }));
                setUnsavedChanges(true);
              }}
              className="w-full input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.bookingAdvanceDays')}
            </label>
            <input
              type="number"
              value={systemSettings.booking_advance_days}
              onChange={(e) => {
                setSystemSettings(prev => ({ ...prev, booking_advance_days: parseInt(e.target.value) }));
                setUnsavedChanges(true);
              }}
              className="w-full input"
              min="1"
              max="90"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.cancellationTimeLimit')}
            </label>
            <input
              type="number"
              value={systemSettings.cancellation_hours}
              onChange={(e) => {
                setSystemSettings(prev => ({ ...prev, cancellation_hours: parseInt(e.target.value) }));
                setUnsavedChanges(true);
              }}
              className="w-full input"
              min="1"
              max="72"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.defaultBookingDuration')}
            </label>
            <select
              value={systemSettings.default_booking_duration}
              onChange={(e) => {
                setSystemSettings(prev => ({ ...prev, default_booking_duration: parseInt(e.target.value) }));
                setUnsavedChanges(true);
              }}
              className="w-full input"
            >
              <option value={30}>30 {t('common.minutes')}</option>
              <option value={60}>60 {t('common.minutes')}</option>
              <option value={90}>90 {t('common.minutes')}</option>
              <option value={120}>120 {t('common.minutes')}</option>
            </select>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-md font-medium text-gray-900 mb-4">{t('settings.automationSettings')}</h4>
          
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={systemSettings.auto_confirm_walkin}
                onChange={(e) => {
                  setSystemSettings(prev => ({ ...prev, auto_confirm_walkin: e.target.checked }));
                  setUnsavedChanges(true);
                }}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">{t('settings.autoConfirmWalkin')}</div>
                <div className="text-xs text-gray-500">{t('settings.autoConfirmWalkinDesc')}</div>
              </div>
            </label>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-8">
          <button
            onClick={() => void handleSystemSave()}
            disabled={!unsavedChanges || isSystemSaving}
            className="btn btn-primary gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" />
            {isSystemSaving ? t('common.saving') : t('settings.saveSettings')}
          </button>
        </div>
      </div>
    </div>
  );

  const renderActivityTab = () => (
    <div className="space-y-6">
      <div className="card card-comfortable">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">{t('settings.operationLogs')}</h3>
          <button
            onClick={() => void loadActivityLogs()}
            className="btn btn-secondary btn-sm"
            disabled={isLogsLoading}
          >
            {isLogsLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {isLogsLoading ? (
          <div className="h-40 flex items-center justify-center text-gray-500">Loading logs...</div>
        ) : activityLogs.length === 0 ? (
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-xl border border-gray-200">
            <div className="text-center text-gray-500">
              <p className="text-sm font-medium">{t('settings.noOperationLogs')}</p>
              <p className="text-xs mt-1">{t('settings.noOperationLogsHint')}</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Time</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Action</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Staff ID</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Changes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {activityLogs.map(log => (
                  <tr key={log.id}>
                    <td className="px-4 py-3 text-gray-700">
                      {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{log.action || '-'}</td>
                    <td className="px-4 py-3 text-gray-700">{log.staff_id ?? '-'}</td>
                    <td className="px-4 py-3 text-gray-700">
                      <span className="line-clamp-2">{String(log.changes || '-')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('settings.title')}</h1>
        <p className="text-gray-500">{t('settings.subtitle')}</p>
      </motion.div>

      {/* 未保存更改提示 */}
      {unsavedChanges && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-xl"
        >
          <AlertTriangle className="h-5 w-5 text-yellow-600" />
          <span className="text-sm font-medium text-yellow-800">
            {t('settings.unsavedChanges')}
          </span>
        </motion.div>
      )}

      {saveNotice && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl"
        >
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span className="text-sm font-medium text-green-800">
            {saveNotice}
            {saveNoticeAt ? ` · ${saveNoticeAt}` : ''}
          </span>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
        {/* 设置导航 */}
        <div className="lg:col-span-1">
          <div className="card card-comfortable sticky top-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">{t('settings.categories')}</h3>
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary-50 text-primary-700 border border-primary-200'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <div>
                      <div className="text-sm font-medium">{tab.label}</div>
                      <div className="text-xs text-gray-500">{tab.description}</div>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* 设置内容 */}
        <div className="lg:col-span-3">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'profile' && renderProfileTab()}
            {activeTab === 'staff' && renderStaffTab()}
            {activeTab === 'system' && renderSystemTab()}
            {activeTab === 'activity' && renderActivityTab()}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
